import { useEffect, useState } from 'react'
import { api, newKey } from '../api/client'
import { useAuth } from '../auth'
import { useFusionConfig } from '../hooks/useFusionConfig'

type FusionState = { cabinetNumber: string; cabinetArea: string; managers: string[] }

export function FusionConfig() {
  const { user } = useAuth()
  const canWrite = Boolean(user?.canWrite)
  const loaded = useFusionConfig()
  const [cabinetNumber, setCabinetNumber] = useState('')
  const [cabinetArea, setCabinetArea] = useState('')
  const [draft, setDraft] = useState<string[]>([])
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!loaded.ready) return
    setCabinetNumber(loaded.state.cabinetNumber)
    setCabinetArea(loaded.state.cabinetArea)
    setDraft(loaded.state.managers)
  }, [loaded.ready, loaded.state])

  useEffect(() => {
    if (loaded.error) setNote(loaded.error)
  }, [loaded.error])

  async function saveCabinet() {
    setBusy(true)
    setNote('')
    try {
      const state = await api.post<FusionState>(
        '/api/fusion/cabinet',
        { cabinetNumber, cabinetArea },
        newKey('cabinet'),
      )
      setCabinetNumber(state.cabinetNumber)
      setCabinetArea(state.cabinetArea)
      setNote('橱柜配置已保存。')
    } catch (e) {
      setNote(e instanceof Error ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  async function saveManagers() {
    setBusy(true)
    setNote('')
    try {
      const state = await api.post<FusionState>('/api/fusion/managers', { managers: draft }, newKey('managers'))
      setDraft(state.managers)
      setNote('管理员名单已保存。')
    } catch (e) {
      setNote(e instanceof Error ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section>
      <header className="page-head compact">
        <div>
          <h1>配置</h1>
          <p className="muted">融合器用的橱柜编号、橱柜区域和管理员名单。名单是整份替换。</p>
        </div>
      </header>
      {!canWrite && <p className="muted">当前账号只能查看，不能改配置。</p>}
      {note && <p className="muted">{note}</p>}
      <div className="grid two">
        <section className="card">
          <h2>橱柜设置</h2>
          <label className="field">
            <span>橱柜编号</span>
            <input
              value={cabinetNumber}
              maxLength={120}
              disabled={!canWrite}
              onChange={(e) => setCabinetNumber(e.target.value)}
            />
          </label>
          <label className="field">
            <span>橱柜区域</span>
            <input
              value={cabinetArea}
              maxLength={120}
              disabled={!canWrite}
              placeholder="www."
              onChange={(e) => setCabinetArea(e.target.value)}
            />
          </label>
          <button type="button" className="primary" disabled={!canWrite || busy} onClick={() => void saveCabinet()}>
            保存橱柜
          </button>
        </section>
        <section className="card">
          <h2>管理员设置</h2>
          <div className="manager-edit">
            {draft.map((name, index) => (
              <div className="row" key={index}>
                <input
                  value={name}
                  maxLength={40}
                  aria-label="管理员姓名"
                  disabled={!canWrite}
                  onChange={(e) => setDraft((list) => list.map((item, i) => (i === index ? e.target.value : item)))}
                />
                <button
                  type="button"
                  disabled={!canWrite}
                  onClick={() => setDraft((list) => list.filter((_, i) => i !== index))}
                >
                  删除
                </button>
              </div>
            ))}
          </div>
          <div className="row">
            <button type="button" disabled={!canWrite || draft.length >= 50} onClick={() => setDraft((list) => [...list, ''])}>
              新增管理员
            </button>
            <button type="button" className="primary" disabled={!canWrite || busy} onClick={() => void saveManagers()}>
              保存名单
            </button>
          </div>
        </section>
      </div>
    </section>
  )
}
